import {
  NotificationDefinition,
  NotificationDefinitionFormValues,
  NotificationDefinitionRow,
  NotificationEntityType,
  NotificationScope,
  NotificationLog,
  NotificationInboxItem,
  EntityItem,
} from "./types";
import { ENTITY_TYPE_LABELS } from "./consts";
import { transformFleetVehicle } from "services/api/fleet/vehicles/transformations";

/*---------------------------------------------------------------
   RESPONSE → DEFINITION (FULL)
-----------------------------------------------------------------*/
export const transformNotificationDefinition = (res: any): NotificationDefinition => ({
  id: res.id,
  title: res.title,
  messageTemplate: res.messageTemplate,
  entityType: res.entityType,
  entityTypeName: resolveEntityTypeLabel(res.entityTypeName),
  scope: res.scope,
  channels: res.channels,
  channelNames: res.channelNames ?? [],
  roles: res.roles ?? [],
  users: res.users ?? [],
  excludedEntityIds: res.excludedEntityIds ?? [],
  includedEntityIds: res.includedEntityIds ?? [],
  rule: res.rule
    ? {
        daysBeforeExpiry: res.rule.daysBeforeExpiry,
        isRepeating: res.rule.isRepeating,
        repeatIntervalDays: res.rule.repeatIntervalDays,
        repeatCount: res.rule.repeatCount,
      }
    : null,
  isCustom: res.isCustom,
  customScheduledAt: res.customScheduledAt,
  notifyIfDateMissing: res.notifyIfDateMissing,
  isActive: res.isActive,
  created: res.created,
  createdByUserId: res.createdByUserId,
});

/*---------------------------------------------------------------
   RESPONSE → ROW (TABLE)
-----------------------------------------------------------------*/
const SCOPE_NAMES: Record<number, string> = {
  [NotificationScope.Group]: "Grupno",
  [NotificationScope.Individual]: "Individualno",
};

const resolveEntityTypeLabel = (name: string): string => {
  const enumValue = NotificationEntityType[name as keyof typeof NotificationEntityType];
  return enumValue != null ? (ENTITY_TYPE_LABELS[enumValue] ?? name) : name;
};

export const transformNotificationDefinitionRow = (res: any): NotificationDefinitionRow => ({
  id: res.id,
  title: res.title,
  messageTemplate: res.messageTemplate ?? "",
  entityType: res.entityType,
  entityTypeName: resolveEntityTypeLabel(res.entityTypeName),
  scopeName: res.scopeName ?? SCOPE_NAMES[res.scope] ?? "-",
  channelNames: res.channelNames ?? [],
  daysBeforeExpiry: res.rule?.daysBeforeExpiry ?? res.daysBeforeExpiry ?? null,
  isCustom: res.isCustom,
  isRepeating: res.rule?.isRepeating ?? false,
  repeatIntervalDays: res.rule?.repeatIntervalDays ?? null,
  repeatCount: res.rule?.repeatCount ?? null,
  notifyIfDateMissing: res.notifyIfDateMissing ?? false,
  customScheduledAt: res.customScheduledAt ?? null,
  recipientCount: res.users?.length ?? 0,
  isActive: res.isActive,
  created: res.created,
  childRows: res.scope === NotificationScope.Individual && res.includedEntities?.length > 0
    ? res.includedEntities.map((e: any) => ({
        id: e.entityId,
        entityId: e.entityId,
        displayName: e.displayName,
        entityType: res.entityType,
        parentEntityId: e.parentEntityId,
      }))
    : undefined,
});

/*---------------------------------------------------------------
   FORM VALUES → API BODY (CREATE/UPDATE)
-----------------------------------------------------------------*/
export const transformNotificationDefinitionOnCreate = (
  formValues: NotificationDefinitionFormValues
): any => ({
  title: formValues.title,
  messageTemplate: formValues.messageTemplate,
  entityType: formValues.entityType,
  scope: Number(formValues.scope),
  channels: formValues.channels,
  isCustom: formValues.isCustom,
  customScheduledAt: formValues.isCustom ? formValues.customScheduledAt : null,
  includedRoleIds: [],
  includedUserIds: formValues.includedUserIds,
  excludedUserIds: [],
  excludedEntityIds: formValues.excludedEntityIds,
  includedEntityIds: formValues.includedEntityIds,
  daysBeforeExpiry: formValues.isCustom ? null : formValues.daysBeforeExpiry,
  isRepeating: formValues.isCustom ? false : formValues.isRepeating,
  repeatIntervalDays: formValues.isCustom ? null : formValues.repeatIntervalDays,
  repeatCount: formValues.isCustom ? null : formValues.repeatCount,
  notifyIfDateMissing: formValues.notifyIfDateMissing,
});

/*---------------------------------------------------------------
   HELPER — parse enum value (handles both numeric and string names)
-----------------------------------------------------------------*/
const parseEnumValue = <T extends Record<string, string | number>>(
  enumObj: T,
  value: any
): number | null => {
  if (value == null) return null;
  if (typeof value === "number") return value;
  // String name → numeric value via reverse mapping
  const mapped = (enumObj as any)[value];
  if (typeof mapped === "number") return mapped;
  // Fallback: try parsing as number
  const num = Number(value);
  return isNaN(num) ? null : num;
};

/*---------------------------------------------------------------
   DEFINITION → FORM VALUES (FOR EDIT)
-----------------------------------------------------------------*/
export const transformDefinitionToForm = (
  def: NotificationDefinition
): NotificationDefinitionFormValues => ({
  title: def.title,
  messageTemplate: def.messageTemplate,
  entityType: parseEnumValue(NotificationEntityType, def.entityType) as NotificationEntityType | null,
  scope: (parseEnumValue(NotificationScope, def.scope) ?? NotificationScope.Group) as NotificationScope,
  channels: def.channels,
  isCustom: def.isCustom,
  customScheduledAt: def.customScheduledAt ? new Date(def.customScheduledAt) : null,
  includedRoleIds: def.roles.filter((r) => !r.isExcluded).map((r) => r.roleId),
  includedUserIds: def.users.filter((u) => !u.isExcluded).map((u) => u.userId),
  excludedUserIds: def.users.filter((u) => u.isExcluded).map((u) => u.userId),
  excludedEntityIds: def.excludedEntityIds,
  includedEntityIds: def.includedEntityIds ?? [],
  daysBeforeExpiry: def.rule?.daysBeforeExpiry ?? null,
  isRepeating: def.rule?.isRepeating ?? false,
  repeatIntervalDays: def.rule?.repeatIntervalDays ?? null,
  repeatCount: def.rule?.repeatCount ?? null,
  notifyIfDateMissing: def.notifyIfDateMissing,
});

/*---------------------------------------------------------------
   LOG
-----------------------------------------------------------------*/
export const transformNotificationLog = (res: any): NotificationLog => ({
  id: res.id,
  vehicle: res.vehicle ? transformFleetVehicle(res.vehicle) : null,
  entityId: res.entityId,
  entityTypeName: resolveEntityTypeLabel(res.entityTypeName),
  entityDisplayName: res.entityDisplayName,
  recipientUserId: res.recipientUserId,
  channelName: res.channelName,
  sentTitle: res.sentTitle,
  sentMessage: res.sentMessage,
  sentTries: res.sentTries,
  sentOnUtc: res.sentOnUtc,
  isSuccess: res.isSuccess,
  errorMessage: res.errorMessage,
  isRead: res.isRead,
  created: res.created,
  definitionTitle: res.definitionTitle,
});

/*---------------------------------------------------------------
   ENTITY ITEM
-----------------------------------------------------------------*/
export const transformEntityItem = (res: any): EntityItem => ({
  id: res.id,
  displayName: res.displayName,
  relevantDate: res.relevantDate,
  hasMissingDate: res.hasMissingDate ?? false,
});

/*---------------------------------------------------------------
   INBOX ITEM
-----------------------------------------------------------------*/
export const transformNotificationInboxItem = (res: any): NotificationInboxItem => ({
  id: res.id,
  sentTitle: res.sentTitle,
  sentMessage: res.sentMessage,
  entityDisplayName: res.entityDisplayName,
  entityId: res.entityId ?? null,
  entityType: res.entityType ?? null,
  parentEntityId: res.parentEntityId ?? null,
  isRead: res.isRead,
  readAt: res.readAt,
  created: res.created,
});
